/**
 * PERMISSION GUARD COMPONENT
 * 
 * Protects pages/features based on required permissions.
 * Redirects to access-denied page or dashboard if user lacks permissions.
 * 
 * Usage:
 * <PermissionGuard requires="payment:view">
 *   <PaymentsPage />
 * </PermissionGuard>
 */

import React, { ReactNode } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { PortalPermission } from '../types/permissions';
import { Box, Center, Heading, Text, Button, VStack } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';

interface PermissionGuardProps {
  children: ReactNode;
  requires?: PortalPermission | PortalPermission[]; // Permission(s) required
  requireAll?: boolean; // If true, ALL permissions required. If false (default), ANY permission required
  forbiddenRoles?: string[]; // Roles that are NOT allowed (e.g., ['assistant'])
  fallback?: ReactNode; // Custom fallback component/message
  redirectTo?: string; // Where to redirect if denied (default: /dashboard)
}

export function PermissionGuard({
  children,
  requires,
  requireAll = false,
  forbiddenRoles = [],
  fallback,
  redirectTo = '/dashboard',
}: PermissionGuardProps) {
  const auth = useAuth();
  const navigate = useNavigate();

  if (auth.loading) {
    return (
      <Center h="100vh">
        <Heading>Loading...</Heading>
      </Center>
    );
  }

  // Check if user's role is forbidden
  if (forbiddenRoles.length > 0 && auth.userProfile) {
    const isForbidden = forbiddenRoles.includes(auth.userProfile.role);
    if (isForbidden) {
      // User's role is forbidden - deny access
      if (fallback) {
        return <>{fallback}</>;
      }

      return (
        <Box minH="100vh" bg="gray.50" py={12} px={4}>
          <Center h="100%">
            <VStack spacing={6} textAlign="center">
              <Box>
                <Heading size="xl" mb={2} color="red.600">
                  Access Denied
                </Heading>
                <Text fontSize="lg" color="gray.600">
                  You do not have permission to access this page.
                </Text>
                <Text fontSize="sm" color="gray.500" mt={2}>
                  Your role: <strong>{auth.userProfile.role}</strong>
                </Text>
              </Box>

              <Button
                colorScheme="brand"
                size="lg"
                onClick={() => navigate(redirectTo)}
              >
                Return to Dashboard
              </Button>
            </VStack>
          </Center>
        </Box>
      );
    }
  }

  // No permissions specified - allow access
  if (!requires) {
    return <>{children}</>;
  }

  // Normalize to array
  const requiredPerms = Array.isArray(requires) ? requires : [requires];

  // Check permissions
  let hasAccess = false;
  if (requireAll) {
    // User must have ALL permissions
    hasAccess = auth.hasAllPermissions(requiredPerms);
  } else {
    // User must have ANY permission
    hasAccess = auth.hasAnyPermission(requiredPerms);
  }

  if (hasAccess) {
    return <>{children}</>;
  }

  // User doesn't have required permissions
  if (fallback) {
    return <>{fallback}</>;
  }

  // Default access denied screen
  return (
    <Box minH="100vh" bg="gray.50" py={12} px={4}>
      <Center h="100%">
        <VStack spacing={6} textAlign="center">
          <Box>
            <Heading size="xl" mb={2} color="red.600">
              Access Denied
            </Heading>
            <Text fontSize="lg" color="gray.600">
              You do not have permission to access this page.
            </Text>
            <Text fontSize="sm" color="gray.500" mt={2}>
              Required permission: {requiredPerms.join(', ')}
            </Text>
            <Text fontSize="sm" color="gray.500">
              Your role: <strong>{auth.userProfile?.role}</strong>
            </Text>
          </Box>

          <Button
            colorScheme="brand"
            size="lg"
            onClick={() => navigate(redirectTo)}
          >
            Return to Dashboard
          </Button>

          {auth.userProfile && (
            <Box fontSize="xs" color="gray.400" mt={4}>
              Your permissions: {auth.userProfile.permissions.length} permissions
            </Box>
          )}
        </VStack>
      </Center>
    </Box>
  );
}

/**
 * Hook to check if current user has specific permission
 * Useful for showing/hiding UI elements
 * 
 * Usage:
 * const canDelete = useCanAccess('property:delete');
 * if (canDelete) {
 *   // Show delete button
 * }
 */
export function useCanAccess(
  permission: PortalPermission | PortalPermission[],
  requireAll: boolean = false
): boolean {
  const auth = useAuth();

  if (requireAll) {
    return auth.hasAllPermissions(Array.isArray(permission) ? permission : [permission]);
  } else {
    return auth.hasAnyPermission(Array.isArray(permission) ? permission : [permission]);
  }
}

/**
 * Utility to safely wrap a component with permission guard
 * Useful for route protection
 * 
 * Usage:
 * <Route path="/payments" element={withPermissionGuard(<PaymentsPage />, 'payment:view')} />
 */
export function withPermissionGuard(
  Component: ReactNode,
  permission: PortalPermission | PortalPermission[]
) {
  return <PermissionGuard requires={permission}>{Component}</PermissionGuard>;
}
